var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import useSWRMutation from "swr/mutation";
import { LogLevel } from "../../Shared/hooks/useLogging/Logging-types";
import { useLogging } from "../../Shared/hooks/useLogging/useLogging";
import { AUTH_SWR_KEYS } from "../Utilities/swrKeys";
import { useCachedSWR } from "../Utilities/useCachedSWR";
import { createPaymentMethod, deactivatePaymentMethod, processPaymentMethodError, setPaymentMethodAsPrimary, updatePaymentMethod, } from "./paymentMethodMutations-utils";
/**
 * SWR mutation hook for creating a new payment method.
 *
 * This hook handles payment method creation via the legacy endpoint:
 * POST /CreatePaymentMethod
 *
 * The backend handles processor-specific logic (Stripe, Authorize.Net, Tap) based on
 * the PaymentProcessor value in the request payload.
 *
 * The mutation automatically updates the SWR cache optimistically with the new payment method.
 *
 * @example
 * ```typescript
 * const { trigger, isMutating, error } = useCreatePaymentMethodMutation();
 *
 * // Create new Stripe payment method
 * const result = await trigger({
 *   isPrimary: true,
 *   paymentProcessor: PaymentProcessor.STRIPE,
 *   stripePaymentMethodId: "pm_123abc",
 * });
 * ```
 */
export const useCreatePaymentMethodMutation = () => {
    const { datadogLog } = useLogging();
    // Get current cached data to pass to the mutation
    const { data: currentData } = useCachedSWR(AUTH_SWR_KEYS.PAYMENT_METHODS);
    const { trigger: triggerMutation, isMutating, error: mutationError, } = useSWRMutation(AUTH_SWR_KEYS.PAYMENT_METHODS, createPaymentMethod, {
        onError: (error) => {
            const processedError = processPaymentMethodError(error);
            datadogLog("Failed to create payment method.", LogLevel.ERROR, undefined, processedError);
        },
        populateCache: true, // Use the mutation result to populate the cache
    });
    // Wrapper to pass current data along with the payment method request
    const trigger = (request) => __awaiter(void 0, void 0, void 0, function* () {
        return triggerMutation({ request, currentData });
    });
    return { trigger, isMutating, error: mutationError };
};
/**
 * SWR mutation hook for updating an existing payment method.
 *
 * This hook handles payment method updates via the legacy endpoint:
 * POST /UpdatePaymentMethod
 *
 * The backend determines the processor and updates accordingly.
 *
 * The mutation automatically updates the SWR cache optimistically with the updated payment method.
 *
 * @example
 * ```typescript
 * const { trigger, isMutating, error } = useUpdatePaymentMethodMutation();
 *
 * // Update existing payment method
 * const result = await trigger({
 *   paymentMethodId: "mtcc_77948",
 *   request: {
 *     paymentProcessor: PaymentProcessor.STRIPE,
 *     stripePaymentMethodId: "pm_456def",
 *   },
 * });
 * ```
 */
export const useUpdatePaymentMethodMutation = () => {
    const { datadogLog } = useLogging();
    // Get current cached data to pass to the mutation
    const { data: currentData } = useCachedSWR(AUTH_SWR_KEYS.PAYMENT_METHODS);
    const { trigger: triggerMutation, isMutating, error: mutationError, } = useSWRMutation(AUTH_SWR_KEYS.PAYMENT_METHODS, updatePaymentMethod, {
        onError: (error) => {
            const processedError = processPaymentMethodError(error);
            datadogLog("Failed to update payment method.", LogLevel.ERROR, undefined, processedError);
        },
        populateCache: true, // Use the mutation result to populate the cache
    });
    // Wrapper to pass current data along with the update payload
    const trigger = (payload) => __awaiter(void 0, void 0, void 0, function* () {
        return triggerMutation(Object.assign(Object.assign({}, payload), { currentData }));
    });
    return { trigger, isMutating, error: mutationError };
};
/**
 * SWR mutation hook for setting a payment method as primary.
 *
 * This hook toggles a payment method as the primary via the legacy endpoint:
 * POST /UpdatePrimaryPaymentMethod
 *
 * This operation updates the isPrimary flag on the target payment method and
 * clears the isPrimary flag on any previously primary payment method.
 *
 * The mutation automatically updates the SWR cache optimistically.
 *
 * @example
 * ```typescript
 * const { trigger, isMutating, error } = useSetPaymentMethodAsPrimaryMutation();
 *
 * // Set payment method as primary
 * const result = await trigger("mtcc_77948");
 * ```
 */
export const useSetPaymentMethodAsPrimaryMutation = () => {
    const { datadogLog } = useLogging();
    // Get current cached data to pass to the mutation
    const { data: currentData } = useCachedSWR(AUTH_SWR_KEYS.PAYMENT_METHODS);
    const { trigger: triggerMutation, isMutating, error: mutationError, } = useSWRMutation(AUTH_SWR_KEYS.PAYMENT_METHODS, setPaymentMethodAsPrimary, {
        onError: (error) => {
            const processedError = processPaymentMethodError(error);
            datadogLog("Failed to set payment method as primary.", LogLevel.ERROR, undefined, processedError);
        },
        populateCache: true, // Use the mutation result to populate the cache
    });
    // Wrapper to pass current data along with the payment method ID
    const trigger = (paymentMethodId) => __awaiter(void 0, void 0, void 0, function* () {
        return triggerMutation({ paymentMethodId, currentData });
    });
    return { trigger, isMutating, error: mutationError };
};
/**
 * SWR mutation hook for deactivating (soft deleting) a payment method.
 *
 * This hook deactivates a payment method via the legacy endpoint:
 * POST /DeactivatePaymentMethod
 *
 * This is a soft delete - the payment method is marked inactive but not physically deleted.
 *
 * The mutation automatically updates the SWR cache by removing the payment method from the list.
 *
 * @example
 * ```typescript
 * const { trigger, isMutating, error } = useDeactivatePaymentMethodMutation();
 *
 * // Deactivate payment method
 * const result = await trigger("mtcc_77948");
 * ```
 */
export const useDeactivatePaymentMethodMutation = () => {
    const { datadogLog } = useLogging();
    // Get current cached data to pass to the mutation
    const { data: currentData } = useCachedSWR(AUTH_SWR_KEYS.PAYMENT_METHODS);
    const { trigger: triggerMutation, isMutating, error: mutationError, } = useSWRMutation(AUTH_SWR_KEYS.PAYMENT_METHODS, deactivatePaymentMethod, {
        onError: (error) => {
            const processedError = processPaymentMethodError(error);
            datadogLog("Failed to deactivate payment method.", LogLevel.ERROR, undefined, processedError);
        },
        populateCache: true, // Use the mutation result to populate the cache
    });
    // Wrapper to pass current data along with the payment method ID
    const trigger = (paymentMethodId) => __awaiter(void 0, void 0, void 0, function* () {
        return triggerMutation({ paymentMethodId, currentData });
    });
    return { trigger, isMutating, error: mutationError };
};
